import axios, { AxiosInstance } from 'axios';
import JSONbigFactory from 'json-bigint';
import closableEnqueueSnackbar from '../components/closableEnqueueSnackbar/closableEnqueueSnackbar';
import i18n from '../i18n';
import { useAuthStore } from '../store/authStore';

const BASE_URL = import.meta.env.VITE_API_URL;
export const CORE_API_SUFFIX = 'digger-core-api/';
export const DIGGER_API_SUFFIX = 'digger-dashboard/api/';
let isHandlingUnauthorized = false;

const handleUnauthorizedResponse = () => {
  const hasActiveSession = !!localStorage.getItem('token');

  if (!hasActiveSession || isHandlingUnauthorized) {
    return;
  }

  isHandlingUnauthorized = true;
  useAuthStore.getState().logout();
  closableEnqueueSnackbar(i18n.t('sessionExpired'), 'error');

  if (window.location.pathname !== '/auth') {
    window.location.assign('/auth');
    return;
  }

  isHandlingUnauthorized = false;
};

function createAxiosInstance(baseURL: string) {
  const JSONbig = JSONbigFactory({ storeAsString: true });

  const instance: AxiosInstance = axios.create({
    baseURL,
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
    },
    transformResponse: [
      (data) => {
        if (typeof data !== 'string' || data.length === 0) return data;
        try {
          return JSONbig.parse(data);
        } catch {
          return data;
        }
      },
    ],
  });

  instance.interceptors.request.use((config) => {
    const token = localStorage.getItem('token');
    const organizationId = localStorage.getItem('organizationId');
    const userId = localStorage.getItem('userId');

    const modifiedConfig = { ...config };

    modifiedConfig.headers = modifiedConfig.headers || {};

    if (typeof modifiedConfig.url === 'string' && typeof baseURL === 'string') {
      try {
        const urlPrefix = new URL(baseURL);

        if (modifiedConfig.url.startsWith(':8080/')) {
          urlPrefix.port = '8080';
          modifiedConfig.baseURL = urlPrefix.toString().replace(/\/+$/, '');
          const requestPath = modifiedConfig.url.replace(/^:8080\/?/, '');
          modifiedConfig.url = requestPath.startsWith('/') ? requestPath : `/${requestPath}`;
        } else if (modifiedConfig.url.startsWith(':8000/')) {
          urlPrefix.port = '8000';
          modifiedConfig.baseURL = urlPrefix.toString().replace(/\/+$/, '');
          const requestPath = modifiedConfig.url.replace(/^:8000/, '');
          modifiedConfig.url = requestPath.startsWith('/') ? requestPath : `/${requestPath}`;
        }
      } catch {
      }
    }

    if (token) {
      modifiedConfig.headers.Authorization = `Bearer ${token}`;
    }

    if (organizationId) {
      modifiedConfig.headers['X-Organization-Id'] = organizationId;
    }

    if (userId) {
      modifiedConfig.headers['X-User-Id'] = userId;
    }

    return modifiedConfig;
  });

  instance.interceptors.response.use(
    (response) => response,
    (error) => {
      if (error?.response?.status === 401) {
        handleUnauthorizedResponse();
      }

      return Promise.reject(error);
    },
  );

  return instance;
}

export const dataFetch = createAxiosInstance(BASE_URL);
